import { IRawStyle, IStyle } from '@fluentui/react';

import { IH2OTheme } from '../../models/components.models';
import { BoxShadowBadge, FontSizes, FontWeights, LineHeights, Sizes } from '../../themes/styles';
import { CommonStyles } from '../../themes/themes';
import { IItemStyles } from '../Item/Item.styles';

export interface ICardStyles {
  root: IStyle;
  content: IStyle;
  thumbnail: IStyle;
  thumbnailHeader: IStyle;
  thumbnailBadgeList: IStyle;
  thumbnailHeaderDivider: IStyle;
  thumbnailHeaderPin: IStyle;
  thumbnailHeaderLike: IStyle;
  thumbnailHeaderLikeLikes: IStyle;
  thumbnailImage: IStyle;
  thumbnailImageDescription: IStyle;
  thumbnailImageDescriptionDetail: IStyle;
  thumbnailStatus: IStyle;
  header: IStyle;
  headerTags: IStyle;
  headerTagsList: IStyle;
  headerTitle: IStyle;
  headerTitleLink: IStyle;
  headerUserName: IStyle;
  headerActions: IStyle;
  headerMenu: IStyle;
}

const thumbnailHeaderIconStyle = (): IRawStyle => ({
  padding: 0,
  alignItems: 'left',
  display: 'flex',
  position: 'relative',
});

export const badgeHeaderStyle = (theme: IH2OTheme): Partial<IItemStyles> => ({
  labelButton: {
    margin: 0,
    padding: `0 4px`,
  },
  root: {
    margin: `0 6px 6px 0`,
    padding: 0,
    backgroundColor: theme.palette?.white,
    boxShadow: BoxShadowBadge,
    flexFlow: 'wrap',
    flexDirection: 'column',
    ...CommonStyles.truncateString,
    maxWidth: 185,
  },
});

export const cardStylesDefault = (theme: IH2OTheme): ICardStyles => ({
  root: {
    backgroundColor: theme.palette?.white,
    borderRadius: Sizes.borderRadius,
    position: 'relative',
    height: '100%',
    ':hover': {
      boxShadow: `0px 3px 10px ${theme.palette?.gray300_a_50}`,
    },
    ':hover .h2o-Card-thumbnailImageDescription': {
      opacity: 1,
      zIndex: 1,
    },
  },
  content: {
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  thumbnail: {
    flex: 1,
    borderRadius: Sizes.borderRadius,
    position: 'relative',
  },
  thumbnailBadgeList: {
    paddingTop: 0,
    minHeight: 2,
    lineHeight: FontSizes.small,
  },
  thumbnailHeader: {
    borderRadius: Sizes.borderRadius,
    display: 'flex',
    position: 'absolute',
    top: 8,
    zIndex: '2',
    '&.has-likes .h2o-Card-thumbnailHeaderPin, &.has-likes .h2o-Card-thumbnailHeaderLike': {
      paddingLeft: 8,
      paddingRight: 8,
    },
    '&.has-likes .h2o-Card-thumbnailHeaderLikeLikes': {
      display: 'inline-block',
    },
  },
  thumbnailHeaderDivider: {
    width: 1,
    height: 24,
    backgroundColor: theme.palette?.gray700,
    marginTop: 4,
  },
  thumbnailHeaderPin: {
    ...thumbnailHeaderIconStyle(),
    marginRight: Sizes.padding / 2,
  },
  thumbnailHeaderLike: {
    ...thumbnailHeaderIconStyle(),
  },
  thumbnailHeaderLikeLikes: {
    fontSize: FontSizes.xxsmall,
    height: '100%',
    lineHeight: Sizes.height,
    paddingLeft: 4,
    textAlign: 'right',
    transition: 'color 0.3s ease-in-out',
  },
  thumbnailImage: {
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundColor: theme.palette?.gray200,
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    borderRadius: Sizes.borderRadius,
    '::after': {
      display: 'block',
      content: "''",
      paddingBottom: '100%',
    },
    '&.loading': {
      backgroundRepeat: 'repeat-y',
      backgroundSize: '100% 1px',
    },
    '&.clickable': {
      cursor: 'pointer',
    },
  },
  thumbnailImageDescription: {
    opacity: 0,
    zIndex: -1,
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    color: theme.palette?.white,
    fontSize: FontSizes.textPrimary,
    letterSpacing: '0px',
    transition: 'opacity 0.3s ease-in-out, z-index 0.5s',
    '::before': {
      content: "''", // Fluent IStyle needs this "''"
      display: 'block',
      position: 'absolute',
      zIndex: -1,
      width: '100%',
      height: '100%',
      background: 'transparent linear-gradient(180deg, #000000 0%, #0000007e 100%)',
    },
  },
  thumbnailImageDescriptionDetail: {
    lineHeight: LineHeights.xlarge,
    overflow: 'auto',
    position: 'absolute',
    maxHeight: '100%',
    width: '100%',
    bottom: 0,
    left: 0,
    padding: `10px 15px`,
  },
  thumbnailStatus: {
    position: 'absolute',
    lineHeight: 24,
    padding: '0 7px',
    bottom: 8,
    fontSize: FontSizes.xxsmall,
    borderRadius: Sizes.borderRadius,
    color: theme.semanticColors?.textQuaternary,
    fontWeight: FontWeights.semiBold,
    boxShadow: BoxShadowBadge,
    ...CommonStyles.truncateString,
    maxWidth: 185,
  },
  header: {
    width: '100%',
  },
  headerTags: {
    alignItems: 'center',
    color: theme.palette?.primary800,
    display: 'flex',
    fontSize: FontSizes.textPrimary,
    fontWeight: FontWeights.semiBold,
    height: 25,
    letterSpacing: '0px',
    overflow: 'hidden',
    paddingTop: 9,
    textTransform: 'uppercase',
  },
  headerTagsList: {
    alignItems: 'center',
    height: '1.1em',
    fontWeight: FontWeights.normal,
    ...CommonStyles.truncateString,
  },
  headerTitle: {
    margin: 0,
    color: theme.palette?.gray900,
    fontSize: FontSizes.medium,
    fontWeight: FontWeights.semiBold,
    letterSpacing: '0px',
    lineHeight: LineHeights.xxlarge,
    ...CommonStyles.truncateString,
  },
  headerTitleLink: {
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  headerUserName: {
    margin: 0,
    color: theme.palette?.gray700,
    fontSize: FontSizes.xxsmall,
    letterSpacing: '0px',
    lineHeight: LineHeights.xxxsmall,
    paddingTop: 5,
    ...CommonStyles.truncateString,
  },
  headerActions: {
    marginTop: 12,
  },
  headerMenu: {
    position: 'absolute',
    right: 16,
  },
});

export const cardStylesNoGap: Partial<ICardStyles> = {
  root: {
    borderRadius: 0,
    ':hover': {
      boxShadow: 'none',
    },
  },
};
